import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { slugify } from "@/lib/slugify";
import {
  COMPLIANCE_DURATION_MONTHS,
  COMPLIANCE_TYPES,
  COMPLIANCE_TYPE_LABEL,
  type ComplianceDocType,
  type Contractor,
  type Property,
  getSupabase,
} from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarPlus, ExternalLink, Plus, Upload, Zap } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type ComplianceRow = {
  id: string;
  property_id: string;
  type: ComplianceDocType;
  file_path: string | null;
  issue_date: string;
  expiry_date: string | null;
  auto_instruct: boolean;
  auto_instruct_contractor_id: string | null;
  property: { display_name: string } | null;
};

const schema = z.object({
  property_id: z.string().uuid("Pick a property"),
  type: z.enum(["CP12", "CP6", "EPC", "EICR"]),
  issue_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
  expiry_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

const eventSchema = z.object({
  property_id: z.string().uuid("Pick a property"),
  type: z.enum(["CP12", "CP6", "EPC", "EICR"]),
  issue_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD")
    .optional()
    .or(z.literal("")),
  expiry_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
});
type EventFormValues = z.infer<typeof eventSchema>;

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function statusFor(daysLeft: number | null): {
  label: string;
  className: string;
} {
  if (daysLeft === null) return { label: "No expiry set", className: "bg-bg-card text-ink-dim" };
  if (daysLeft < 0)
    return {
      label: `Expired ${Math.abs(daysLeft)}d ago`,
      className: "bg-red-500/15 text-red-200 border border-red-500/40",
    };
  if (daysLeft <= 30)
    return {
      label: `Due in ${daysLeft}d`,
      className: "bg-red-500/15 text-red-200 border border-red-500/40",
    };
  if (daysLeft <= 60)
    return {
      label: `Due in ${daysLeft}d`,
      className: "bg-amber-500/15 text-amber-200 border border-amber-500/40",
    };
  return {
    label: `Expires in ${daysLeft}d`,
    className: "bg-emerald-500/10 text-emerald-200 border border-emerald-500/30",
  };
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
}

function addMonthsIso(iso: string, months: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function AdminCompliance() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [docs, setDocs] = useState<ComplianceRow[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const { fetchUrl } = useSignedUrl();

  const openPdf = useCallback(
    async (path: string) => {
      setOpeningPath(path);
      setOpenError(null);
      try {
        const { url, error } = await fetchUrl(path);
        if (error || !url) {
          setOpenError(error ?? "Could not generate signed URL");
          return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
      } finally {
        setOpeningPath(null);
      }
    },
    [fetchUrl],
  );

  const refresh = useCallback(async () => {
    const supabase = getSupabase();
    const [props, docsResp, contractorsResp] = await Promise.all([
      supabase.from("properties").select("*").order("display_name", { ascending: true }),
      supabase
        .from("compliance_documents")
        .select(
          "id, property_id, type, file_path, issue_date, expiry_date, auto_instruct, auto_instruct_contractor_id, property:properties(display_name)",
        )
        .order("expiry_date", { ascending: true, nullsFirst: false }),
      supabase.from("contractors").select("*").eq("is_active", true).order("name"),
    ]);
    setProperties((props.data as Property[] | null) ?? []);
    setDocs((docsResp.data as unknown as ComplianceRow[] | null) ?? []);
    setContractors((contractorsResp.data as Contractor[] | null) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <LoadingPanel />;

  // Group docs by property for the dashboard.
  const byProperty = new Map<string, ComplianceRow[]>();
  for (const d of docs)
    byProperty.set(d.property_id, [...(byProperty.get(d.property_id) ?? []), d]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">Compliance</h2>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowEventForm((v) => !v);
              if (!showEventForm) setShowForm(false);
            }}
          >
            <CalendarPlus size={14} />
            {showEventForm ? "Cancel" : "Add event"}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setShowForm((v) => !v);
              if (!showForm) setShowEventForm(false);
            }}
          >
            <Plus size={14} />
            {showForm ? "Cancel" : "Upload document"}
          </Button>
        </div>
      </div>

      {showForm ? (
        <UploadForm
          properties={properties}
          onUploaded={() => {
            setShowForm(false);
            void refresh();
          }}
        />
      ) : null}

      {showEventForm ? (
        <EventForm
          properties={properties}
          onSaved={() => {
            setShowEventForm(false);
            void refresh();
          }}
        />
      ) : null}

      {openError ? (
        <p role="alert" className="text-sm text-red-300">
          {openError}
        </p>
      ) : null}

      {properties.length === 0 ? (
        <p className="text-sm text-ink-dim">No properties yet.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {properties.map((p) => {
            const items = (byProperty.get(p.id) ?? []).sort((a, b) => a.type.localeCompare(b.type));
            // Show the rows the property actually has, plus any standard
            // types not yet on file (so admins see "Not on file" prompts).
            const presentTypes = items.map((it) => it.type);
            const types = Array.from(
              new Set<ComplianceDocType>([...presentTypes, ...COMPLIANCE_TYPES]),
            );
            return (
              <Card key={p.id}>
                <CardHeader>
                  <CardTitle>{p.display_name}</CardTitle>
                  <CardDescription>
                    {[p.line1, p.line2, p.postcode].filter(Boolean).join(", ")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  {types.map((type) => {
                    const doc = items.find((it) => it.type === type) ?? null;
                    const days = daysUntil(doc?.expiry_date ?? null);
                    const s = doc
                      ? statusFor(days)
                      : {
                          label: "Not on file",
                          className: "bg-bg-card text-ink-dim border border-line",
                        };
                    return (
                      <div
                        key={type}
                        className="flex flex-col gap-2 rounded-xl border border-line px-3 py-2"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-ink">
                              {COMPLIANCE_TYPE_LABEL[type] ?? type}
                            </span>
                            <span className="text-xs text-ink-dim">
                              {doc
                                ? `Issued ${fmtDate(doc.issue_date)} · Expires ${fmtDate(doc.expiry_date)}${doc.file_path ? "" : " · No certificate yet"}`
                                : "—"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-md ${s.className}`}>
                              {s.label}
                            </span>
                            {doc?.file_path ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={openingPath === doc.file_path}
                                onClick={() => {
                                  if (doc.file_path) openPdf(doc.file_path);
                                }}
                              >
                                <ExternalLink size={14} />
                                {openingPath === doc.file_path ? "Opening…" : "Open"}
                              </Button>
                            ) : null}
                          </div>
                        </div>
                        {doc ? (
                          <AutoInstructControls
                            doc={doc}
                            contractors={contractors}
                            onChanged={() => void refresh()}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UploadForm({
  properties,
  onUploaded,
}: {
  properties: Property[];
  onUploaded: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Auto-fill expiry when issue date + type are set, using statutory durations.
  const issue = watch("issue_date");
  const type = watch("type");
  const expiry = watch("expiry_date");
  useEffect(() => {
    if (issue && type && /^\d{4}-\d{2}-\d{2}$/.test(issue) && !expiry) {
      setValue("expiry_date", addMonthsIso(issue, COMPLIANCE_DURATION_MONTHS[type]));
    }
  }, [issue, type, expiry, setValue]);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    setSuccess(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setGlobalError("Choose a PDF file.");
      return;
    }
    if (file.type !== "application/pdf") {
      setGlobalError("Only PDF files are allowed.");
      return;
    }
    const supabase = getSupabase();
    const safeName = slugify(file.name.replace(/\.pdf$/i, ""));
    const filePath = `${values.property_id}/${values.type}/${Date.now()}-${safeName}.pdf`;

    const { error: upErr } = await supabase.storage
      .from("compliance")
      .upload(filePath, file, { contentType: "application/pdf" });
    if (upErr) {
      setGlobalError(upErr.message);
      return;
    }
    const { data: inserted, error: dbErr } = await supabase
      .from("compliance_documents")
      .insert({
        property_id: values.property_id,
        type: values.type as ComplianceDocType,
        file_path: filePath,
        issue_date: values.issue_date,
        expiry_date: values.expiry_date || null,
      })
      .select("id")
      .single();
    if (dbErr || !inserted) {
      setGlobalError(dbErr?.message ?? "Insert failed");
      return;
    }
    setSuccess(`Uploaded ${file.name}. Extracting dates…`);
    // Fire-and-forget vision extract. The cron only cares about expiry_date,
    // so even if the agent takes a few seconds the page state is fine.
    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;
    void fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/compliance-cert-extract`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ document_id: inserted.id }),
    }).catch(() => {});
    reset();
    if (fileRef.current) fileRef.current.value = "";
    setTimeout(onUploaded, 1200);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload compliance document</CardTitle>
        <CardDescription>
          PDF only, max 20 MB. Expiry auto-fills from statutory duration when blank (CP12 12 months,
          EICR 5 years, EPC 10 years).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid sm:grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field id="property_id" label="Property" error={errors.property_id?.message}>
            <select
              id="property_id"
              className="h-11 w-full rounded-xl border border-line bg-bg-card px-3 text-base md:text-[15px] text-ink"
              {...register("property_id")}
            >
              <option value="">Select…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field id="type" label="Type" error={errors.type?.message}>
            <select
              id="type"
              className="h-11 w-full rounded-xl border border-line bg-bg-card px-3 text-base md:text-[15px] text-ink"
              {...register("type")}
            >
              <option value="">Select…</option>
              {COMPLIANCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {COMPLIANCE_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field id="issue_date" label="Issue date" error={errors.issue_date?.message}>
            <Input id="issue_date" type="date" {...register("issue_date")} />
          </Field>
          <Field
            id="expiry_date"
            label="Expiry date (auto-fills)"
            error={errors.expiry_date?.message}
          >
            <Input id="expiry_date" type="date" {...register("expiry_date")} />
          </Field>
          <Field id="file" label="PDF" className="sm:col-span-2">
            <input
              id="file"
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="text-sm text-ink-dim"
            />
          </Field>
          {globalError ? (
            <p role="alert" className="text-xs text-red-300 sm:col-span-2">
              {globalError}
            </p>
          ) : null}
          {success ? (
            <p role="status" className="text-xs text-emerald-300 sm:col-span-2">
              {success}
            </p>
          ) : null}
          <div className="sm:col-span-2 flex">
            <Button type="submit" disabled={isSubmitting}>
              <Upload size={14} />
              {isSubmitting ? "Uploading…" : "Upload document"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EventForm({
  properties,
  onSaved,
}: {
  properties: Property[];
  onSaved: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EventFormValues>({ resolver: zodResolver(eventSchema) });
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    setSuccess(null);
    const supabase = getSupabase();
    // file_path is now nullable - we set it when the cert is later uploaded.
    // issue_date defaults to today if blank (so the row passes the existing
    // not-null constraint while leaving expiry as the source of truth).
    const issueDate = values.issue_date || new Date().toISOString().slice(0, 10);
    const { error: dbErr } = await supabase.from("compliance_documents").insert({
      property_id: values.property_id,
      type: values.type as ComplianceDocType,
      issue_date: issueDate,
      expiry_date: values.expiry_date,
      file_path: null,
    });
    if (dbErr) {
      setGlobalError(dbErr.message);
      return;
    }
    setSuccess("Compliance event scheduled.");
    reset();
    setTimeout(onSaved, 800);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schedule a compliance event</CardTitle>
        <CardDescription>
          For events without a certificate yet (e.g. a CP12 service due next month). Upload the
          certificate later and the renewal date will be re-extracted from it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid sm:grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field id="event_property_id" label="Property" error={errors.property_id?.message}>
            <select
              id="event_property_id"
              className="h-11 w-full rounded-xl border border-line bg-bg-card px-3 text-base md:text-[15px] text-ink"
              {...register("property_id")}
            >
              <option value="">Select…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </Field>
          <Field id="event_type" label="Type" error={errors.type?.message}>
            <select
              id="event_type"
              className="h-11 w-full rounded-xl border border-line bg-bg-card px-3 text-base md:text-[15px] text-ink"
              {...register("type")}
            >
              <option value="">Select…</option>
              {COMPLIANCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {COMPLIANCE_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            id="event_issue_date"
            label="Last issued (optional)"
            error={errors.issue_date?.message}
          >
            <Input id="event_issue_date" type="date" {...register("issue_date")} />
          </Field>
          <Field id="event_expiry_date" label="Renewal due" error={errors.expiry_date?.message}>
            <Input id="event_expiry_date" type="date" {...register("expiry_date")} />
          </Field>
          {globalError ? (
            <p role="alert" className="text-xs text-red-300 sm:col-span-2">
              {globalError}
            </p>
          ) : null}
          {success ? (
            <p role="status" className="text-xs text-emerald-300 sm:col-span-2">
              {success}
            </p>
          ) : null}
          <div className="sm:col-span-2 flex">
            <Button type="submit" disabled={isSubmitting}>
              <CalendarPlus size={14} />
              {isSubmitting ? "Scheduling…" : "Schedule event"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AutoInstructControls({
  doc,
  contractors,
  onChanged,
}: {
  doc: ComplianceRow;
  contractors: Contractor[];
  onChanged: () => void;
}) {
  const [armed, setArmed] = useState(doc.auto_instruct);
  const [contractorId, setContractorId] = useState(doc.auto_instruct_contractor_id ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function update(next: { armed: boolean; contractorId: string }) {
    setError(null);
    setSaving(true);
    try {
      const sb = getSupabase();
      const { error: upErr } = await sb
        .from("compliance_documents")
        .update({
          auto_instruct: next.armed,
          auto_instruct_contractor_id: next.contractorId || null,
        })
        .eq("id", doc.id);
      if (upErr) {
        setError(upErr.message);
        return;
      }
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-line/60">
      <label className="inline-flex items-center gap-2 text-xs text-ink-dim">
        <input
          type="checkbox"
          checked={armed}
          disabled={saving}
          onChange={(e) => {
            const next = e.target.checked;
            setArmed(next);
            void update({ armed: next, contractorId });
          }}
        />
        <Zap size={12} className={armed ? "text-amber-300" : "text-ink-dim"} />
        <span>Auto-instruct on renewal</span>
      </label>
      <select
        className="h-8 text-xs px-2 rounded-md border border-line bg-bg-card text-ink flex-1 sm:max-w-[260px]"
        value={contractorId}
        disabled={saving}
        onChange={(e) => {
          const next = e.target.value;
          setContractorId(next);
          void update({ armed, contractorId: next });
        }}
      >
        <option value="">— pick contractor —</option>
        {contractors.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {c.compliance_capabilities.length ? ` · ${c.compliance_capabilities.join("/")}` : ""}
          </option>
        ))}
      </select>
      {error ? <span className="text-xs text-red-300">{error}</span> : null}
    </div>
  );
}
